import React, { useContext, useEffect, useMemo, useState } from 'react';
import { If } from 'components';
import { useAppDispatch, useAppSelector } from 'hooks';
import { selectCurrentTeam } from 'containers/app/appSlice';
import {
  useAddCustomerAddressMutation,
  useEditCustomerAddressMutation,
  useLazyGetRecentSearchedCustomersQuery,
  useUpdateCustomerMutation
} from 'containers/customers/api';
import AddressModal from 'containers/jobs/components/AddressModal';
import { ADDRESS_TYPE } from 'containers/customers/constants';
import { CreateEstimateErrorTypes } from 'containers/estimates/constants';
import {
  removeEstimateValidationErrors,
  selectBillingAddress,
  selectEstimateAutoFilledData,
  selectEstimateCustomer,
  selectEstimateValidationErrors,
  selectServiceAddress,
  updateAutoFilledData,
  updateBillingAddress,
  updateEstimateCustomer,
  updateServiceAddress
} from '../../estimatesSlice';
import { getEstimateValidationErrors } from '../../utils';
import { CreateEstimateContext } from '../CreateEstimate';
import EstimateFor from './EstimateFor';
import CustomerDetailsModal from 'containers/jobs/create-edit-job/components/CustomerDetailsModal';
import { AddCustomerAddressPayload, CustomerObject } from 'containers/customers/customers.model';
import { CreateCustomerModalTypes } from 'constants/CustomerConstants';
import { CreateEditEstimateNavEnum } from 'containers/estimates/constants';
import CreateCustomer from 'containers/create-customer/CreateCustomer';
import { API_SOURCE } from 'constants/common';
import { checkIsCustomerAutoFilled, checkIsServiceAddressAutoFilled } from 'containers/invoices/create-invoice/util';
import { getAddressAsString } from 'containers/jobs/utils';
import AutoFilledIndicator from 'components/auto-filled-indicator/AutoFilledIndicator';

const EstimateCustomer: React.FC = () => {
  const { selectedEstimateTab, setSelectedEstimateTab } = useContext(CreateEstimateContext);
  const [openCustomerDetailsModal, setOpenCustomerDetailsModal] = useState(false);
  const [openCreateCustomerModal, setOpenCreateCustomerModal] = useState({
    customer: null,
    isOpen: false,
    type: CreateCustomerModalTypes.EDIT_CUSTOMER
  });
  const [openAddressModal, setOpenAddressModal] = useState({ isOpen: false, type: null, address: null });
  const [customerDetails, setCustomerDetails] = useState(null);
  const [hasChanged, setHasChanged] = useState({ customer: false, serviceAddress: false, billingAddress: false });

  const dispatch = useAppDispatch();
  const currentTeam = useAppSelector(selectCurrentTeam);
  const selectedCustomer = useAppSelector(selectEstimateCustomer);
  const selectedServiceAddress = useAppSelector(selectServiceAddress);
  const selectedBillingAddress = useAppSelector(selectBillingAddress);
  const autoFilledData = useAppSelector(selectEstimateAutoFilledData);
  const validationErrors = useAppSelector(selectEstimateValidationErrors);
  const customerError = getEstimateValidationErrors(validationErrors, CreateEstimateErrorTypes.CUSTOMER);
  const serviceAddressError = getEstimateValidationErrors(validationErrors, CreateEstimateErrorTypes.SERVICE_ADDRESS);
  const customerSectionError = customerError || serviceAddressError;

  const [getRecentSearchedCustomersQuery, { data: recentCustomersData }] = useLazyGetRecentSearchedCustomersQuery();
  const [updateCustomer, { isSuccess: updateCustomerSuccess, data: updatedCustomerData }] = useUpdateCustomerMutation();
  const [addCustomerAddressMutation, { isSuccess: isAddAddressSuccess, isLoading: isLoadingAddAddress }] =
    useAddCustomerAddressMutation();
  const [editCustomerAddressMutation, { isSuccess: isEditAddressSuccess, isLoading: isLoadingEditAddress }] =
    useEditCustomerAddressMutation();

  const getRecentSearchedCustomers = () => {
    getRecentSearchedCustomersQuery({ teamId: currentTeam.id });
  };

  useEffect(() => {
    if (currentTeam?.id) {
      getRecentSearchedCustomers();
    }
  }, [currentTeam?.id]);

  useEffect(() => {
    if (isEditAddressSuccess || isAddAddressSuccess) {
      getRecentSearchedCustomers();
    }
  }, [isEditAddressSuccess, isAddAddressSuccess, isLoadingAddAddress, isLoadingEditAddress]);

  useEffect(() => {
    if (updateCustomerSuccess) {
      dispatch(updateEstimateCustomer(updatedCustomerData));
      dispatch(removeEstimateValidationErrors(CreateEstimateErrorTypes.CUSTOMER));
      setHasChanged(prev => ({ ...prev, customer: false }));
    }
  }, [updateCustomerSuccess, updatedCustomerData]);

  useEffect(() => {
    initialiseCustomerDetails();
  }, [selectedCustomer, selectedServiceAddress, selectedBillingAddress]);

  const handleOpenCustomerDetailsModal = () => {
    setHasChanged({ customer: false, serviceAddress: false, billingAddress: false });
    setOpenCustomerDetailsModal(true);
  };

  const handleCloseCustomerDetailsModal = () => {
    setOpenCustomerDetailsModal(false);
    initialiseCustomerDetails();
  };

  const handleOpenAddressModal = ({ type, isOpen, address }) => {
    setOpenAddressModal({ isOpen, type, address });
  };

  const handleCloseAddressModal = () => {
    setOpenAddressModal({ isOpen: false, type: null, address: null });
  };

  const handleAddressAddition = addressDetails => {
    if (openAddressModal.type === ADDRESS_TYPE.SERVICE) {
      dispatch(updateServiceAddress(addressDetails));
      setHasChanged(prev => ({ ...prev, serviceAddress: true }));
    } else if (openAddressModal.type === ADDRESS_TYPE.BILLING) {
      dispatch(updateBillingAddress(addressDetails));
      setHasChanged(prev => ({ ...prev, billingAddress: true }));
    }
    handleCloseAddressModal();
  };

  const handleRemoveCustomerFromEstimate = () => {
    dispatch(updateEstimateCustomer(null));
    dispatch(updateServiceAddress(null));
    dispatch(updateBillingAddress(null));
    setHasChanged({ customer: false, serviceAddress: false, billingAddress: false });
    // Reset isAnyCustomerData flag to behave like existing flow without autofill
    if (autoFilledData?.isAnyCustomerData) {
      dispatch(updateAutoFilledData({ isAnyCustomerData: false }));
    }
  };

  const handleUpdateCustomerDetails = async (
    isServiceAddressEdit: boolean,
    isBillingAddressEdit: boolean,
    isSameAsServiceAddress: boolean
  ) => {
    if (autoFilledData?.isAnyCustomerData && !autoFilledData.customer?.id) {
      // Customer from autofill with no ID - update state directly without API calls
      const updatedCustomer = {
        ...selectedCustomer,
        ...(hasChanged.customer ? customerDetails : {}),
        isBillingSameAsService: isSameAsServiceAddress
      };

      dispatch(updateEstimateCustomer(updatedCustomer));

      if (hasChanged.serviceAddress && customerDetails.serviceAddress?.length > 0) {
        dispatch(updateServiceAddress(customerDetails.serviceAddress[0]));
      }
      if (hasChanged.billingAddress) {
        dispatch(updateBillingAddress(customerDetails.billingAddress));
      }
    } else {
      // Customer with ID - follow existing pattern with API calls
      let serviceAddressId = customerDetails?.serviceAddress?.[0]?.id ?? 0;
      if (hasChanged.customer) {
        const customer = {
          teamId: currentTeam.id.toString(),
          customerId: customerDetails.ID,
          name: customerDetails.name,
          lastName: customerDetails.lastName,
          companyName: customerDetails.companyName,
          phoneNumber: customerDetails.phoneNumber,
          email: customerDetails.email,
          referenceNumber: customerDetails.referenceNumber,
          note: customerDetails.note,
          source: API_SOURCE.WEB
        };
        await updateCustomer(customer).unwrap();
      }

      if (hasChanged.serviceAddress && customerDetails.serviceAddress?.length > 0) {
        const serviceAddress: AddCustomerAddressPayload = {
          teamId: currentTeam.id.toString(),
          customerId: customerDetails.ID,
          type: ADDRESS_TYPE.SERVICE,
          street: customerDetails.serviceAddress[0].street,
          city: customerDetails.serviceAddress[0].city,
          state: customerDetails.serviceAddress[0].state,
          apartment: customerDetails.serviceAddress[0].apartment,
          zipCode: customerDetails.serviceAddress[0].zipCode,
          country: customerDetails.serviceAddress[0].country,
          addressNote: customerDetails.serviceAddress[0].addressNote?.trim(),
          addressName: customerDetails.serviceAddress[0].addressName,
          latitude: customerDetails.serviceAddress[0].latitude,
          longitude: customerDetails.serviceAddress[0].longitude,
          isBillingSameAsService: false,
          addressId: 0,
          source: API_SOURCE.WEB
        };
        if (isServiceAddressEdit) {
          const address = await editCustomerAddressMutation({
            id: customerDetails.serviceAddress[0].id.toString(),
            body: serviceAddress
          }).unwrap();
          dispatch(updateServiceAddress(address));
        } else {
          const address = await addCustomerAddressMutation(serviceAddress).unwrap();
          serviceAddressId = address.id;
          dispatch(updateServiceAddress(address));
        }
      }

      if (hasChanged.billingAddress || isBillingAddressEdit) {
        const billingAddress: AddCustomerAddressPayload = {
          teamId: currentTeam.id.toString(),
          customerId: customerDetails.ID,
          type: ADDRESS_TYPE.BILLING,
          street: customerDetails.billingAddress ? customerDetails.billingAddress.street : '',
          city: customerDetails.billingAddress ? customerDetails.billingAddress.city : '',
          state: customerDetails.billingAddress ? customerDetails.billingAddress.state : '',
          apartment: customerDetails.billingAddress ? customerDetails.billingAddress.apartment : '',
          zipCode: customerDetails.billingAddress ? customerDetails.billingAddress.zipCode : '',
          country: customerDetails.billingAddress ? customerDetails.billingAddress.country : '',
          addressNote: customerDetails.billingAddress ? customerDetails.billingAddress.addressNote?.trim() : '',
          addressName: customerDetails.billingAddress ? customerDetails.billingAddress.addressName : '',
          latitude: customerDetails.billingAddress ? customerDetails.billingAddress.latitude : 0,
          longitude: customerDetails.billingAddress ? customerDetails.billingAddress.longitude : 0,
          isBillingSameAsService: isSameAsServiceAddress,
          addressId: serviceAddressId,
          source: API_SOURCE.WEB
        };
        if (isSameAsServiceAddress || !isBillingAddressEdit) {
          const address = await addCustomerAddressMutation(billingAddress).unwrap();
          dispatch(updateBillingAddress(address));
        } else {
          const address = await editCustomerAddressMutation({
            id: customerDetails.billingAddress.id.toString(),
            body: billingAddress
          }).unwrap();
          dispatch(updateBillingAddress(address));
        }
      }
      dispatch(updateEstimateCustomer({ ...customerDetails, isBillingSameAsService: isBillingAddressEdit }));
    }
    setHasChanged({ customer: false, serviceAddress: false, billingAddress: false });
    handleCloseCustomerDetailsModal();
  };

  const handleOpenCustomerModal = customer => {
    setOpenCreateCustomerModal({ isOpen: true, customer, type: CreateCustomerModalTypes.EDIT_CUSTOMER });
  };

  const handleCloseCustomerModal = () => {
    setOpenCreateCustomerModal({ isOpen: false, customer: null, type: CreateCustomerModalTypes.EDIT_CUSTOMER });
  };

  const handleCustomerAdd = (customerObject: CustomerObject) => {
    setCustomerDetails(prev => ({ ...prev, ...customerObject }));
    setHasChanged(prev => ({ ...prev, customer: true }));
    handleCloseCustomerModal();
  };

  const handleToggleBillingAddressCheck = (isSameAsServiceAddress: boolean) => {
    if (isSameAsServiceAddress) {
      setCustomerDetails(prev => ({ ...prev, billingAddress: prev.serviceAddress[0] }));
    } else {
      setCustomerDetails(prev => ({ ...prev, billingAddress: selectedBillingAddress ?? null }));
    }
    setHasChanged(prev => ({ ...prev, billingAddress: true }));
  };

  const initialiseCustomerDetails = () => {
    const customer = { ...selectedCustomer };
    customer.serviceAddress = selectedServiceAddress ? [selectedServiceAddress] : [];
    customer.billingAddress = selectedBillingAddress ?? null;
    setCustomerDetails(customer);
  };

  const isTabSelected = selectedEstimateTab === CreateEditEstimateNavEnum.CUSTOMER;
  const getBorderStyles = () => {
    if (customerSectionError) {
      return 'border-error';
    }
    if (isTabSelected) {
      return 'border-secondary';
    }
    return 'border-borderGray';
  };

  const isCustomerAutoFilled = useMemo(() => {
    const autoFilledCustomer = autoFilledData?.isAnyCustomerData ? autoFilledData?.customer : null;
    const customerWithBillingAddress = selectedCustomer
      ? { ...selectedCustomer, billingAddress: selectedBillingAddress ?? null }
      : null;
    return checkIsCustomerAutoFilled(autoFilledCustomer as any, customerWithBillingAddress, getAddressAsString);
  }, [autoFilledData?.customer, autoFilledData?.isAnyCustomerData, selectedCustomer, selectedBillingAddress]);

  const isServiceAddressAutoFilled = useMemo(() => {
    const autoFilledServiceAddress = autoFilledData?.customer?.serviceAddress?.[0] ?? null;
    return checkIsServiceAddressAutoFilled(autoFilledServiceAddress as any, selectedServiceAddress, getAddressAsString);
  }, [autoFilledData?.customer?.serviceAddress, selectedServiceAddress]);

  const isCustomerOrServiceAddressAutoFilled = isCustomerAutoFilled || isServiceAddressAutoFilled;

  return (
    <div className="w-full" onFocus={() => setSelectedEstimateTab(CreateEditEstimateNavEnum.CUSTOMER)}>
      <div className={`mb-2 flex text-xl font-bold  ${isTabSelected ? 'text-primaryBtn' : 'text-heading'}`}>
        Customer<span className="text-base text-error">*</span>{' '}
        <AutoFilledIndicator isAutoFilled={isCustomerOrServiceAddressAutoFilled} className="mt-1.5" />
      </div>
      <div className={`relative rounded-md border ${getBorderStyles()} py-6 px-8`}>
        <EstimateFor
          handleOpenCustomerDetailsModal={handleOpenCustomerDetailsModal}
          handleRemoveCustomerFromEstimate={handleRemoveCustomerFromEstimate}
          recentCustomers={recentCustomersData?.customers || []}
        />
      </div>
      {customerSectionError ? (
        <div className="mt-2 whitespace-pre-line text-sbase font-semibold text-error" role="alert" aria-live="polite">
          {customerSectionError}
        </div>
      ) : null}

      {openAddressModal.isOpen && (
        <AddressModal
          isEdit={Boolean(openAddressModal.address)}
          type={openAddressModal.type}
          onClose={handleCloseAddressModal}
          initialAddress={openAddressModal.address}
          onAddAddress={handleAddressAddition}
          isLoading={isLoadingAddAddress || isLoadingEditAddress}
        />
      )}
      {openCustomerDetailsModal && (
        <CustomerDetailsModal
          onOpenAddressModal={handleOpenAddressModal}
          customer={customerDetails}
          onClose={handleCloseCustomerDetailsModal}
          onUpdateCustomerDetails={handleUpdateCustomerDetails}
          hasValuesChanged={hasChanged.customer || hasChanged.serviceAddress || hasChanged.billingAddress}
          isUpdateInProgress={false}
          onOpenCustomerModal={handleOpenCustomerModal}
          onToggleBillingAddressCheck={handleToggleBillingAddressCheck}
        />
      )}
      <If condition={openCreateCustomerModal.isOpen}>
        <CreateCustomer
          open={openCreateCustomerModal.isOpen}
          onClose={handleCloseCustomerModal}
          panelType={openCreateCustomerModal.type}
          customerData={openCreateCustomerModal.customer}
          customerDataFetchSuccess={true}
          customerDataFetchLoading={false}
          onAddCustomer={handleCustomerAdd}
          defaultName={''}
        />
      </If>
    </div>
  );
};

export default EstimateCustomer;
